import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toEmbeddableMediaUrl } from '../../utils/mediaEmbeds';

const PUSHER_KEY = process.env.REACT_APP_PUSHER_KEY || '';
const PUSHER_CLUSTER = process.env.REACT_APP_PUSHER_CLUSTER || 'mt1';
const PUSHER_HOST = process.env.REACT_APP_PUSHER_HOST;
const PUSHER_PORT = process.env.REACT_APP_PUSHER_PORT;
const PUSHER_SCHEME = process.env.REACT_APP_PUSHER_SCHEME || 'https';
const PLAYBACK_CHANNEL = process.env.REACT_APP_PUSHER_PLAYBACK_CHANNEL || 'tv.playback';
const CALENDAR_EMBED_URL = process.env.REACT_APP_PUBLIC_CALENDAR_EMBED || '';
const resolveDefaultHomeUrl = () => {
  if (typeof window === 'undefined') return '/';
  const explicitBase = process.env.PUBLIC_URL || '';
  let appBase = explicitBase;
  if (!appBase || appBase === '/') {
    const path = window.location.pathname || '/';
    appBase = path === '/app' || path.startsWith('/app/') ? '/app' : '/';
  }
  const normalizedBase = appBase.endsWith('/') ? appBase : `${appBase}/`;
  return new URL(normalizedBase, window.location.origin).toString();
};
const PUBLIC_HOMEPAGE_URL = resolveDefaultHomeUrl();
const formatTime = (value) =>
  value ? new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';

const formatDay = (value) =>
  value ? new Date(value).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' }) : '';

const getElapsedProgramSeconds = (slot) => {
  if (!slot?.starts_at) return 0;

  const startMs = new Date(slot.starts_at).getTime();
  if (!Number.isFinite(startMs)) return 0;

  const nowMs = Date.now();
  let elapsed = Math.floor((nowMs - startMs) / 1000);

  if (slot?.ends_at) {
    const endMs = new Date(slot.ends_at).getTime();
    if (Number.isFinite(endMs)) {
      const duration = Math.max(0, Math.floor((endMs - startMs) / 1000));
      elapsed = Math.min(elapsed, duration);
    }
  }

  return Math.max(0, elapsed);
};

const seekVideoForMode = (video, mode, slot) => {
  if (!video) return;

  try {
    if (mode === 'start') {
      video.currentTime = 0;
      return;
    }

    const liveOffset = getElapsedProgramSeconds(slot);
    if (!Number.isFinite(liveOffset) || liveOffset <= 0) {
      video.currentTime = 0;
      return;
    }

    const seekTo =
      Number.isFinite(video.duration) && video.duration > 0
        ? Math.min(liveOffset, Math.max(video.duration - 1, 0))
        : liveOffset;

    video.currentTime = seekTo;
  } catch (_) {
    // Ignore seek failures on non-seekable streams.
  }
};

const PublicChannel = () => {
  const [state, setState] = useState({ current: null, upcoming: [] });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const [playbackMode, setPlaybackMode] = useState('live');
  const pollRef = useRef(null);
  const videoRef = useRef(null);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get('/tv/playback-state', { params: { next: 8 } });
      setState({
        current: data.current ?? null,
        upcoming: data.upcoming ?? [],
      });
      setError('');
    } catch (err) {
      setError(err.response?.data?.message ?? 'Unable to load playback state.');
    } finally {
      setLoading(false);
    }
  };

  const loadNextArchiveItem = useCallback(async () => {
    const { data } = await axios.get('/tv/archive/next');
    setState({
      current: data.current ?? null,
      upcoming: [],
    });
    setError('');
  }, []);

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 30_000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  useEffect(() => {
    if (!PUSHER_KEY || typeof window === 'undefined' || !window.Pusher) {
      return undefined;
    }

    const options = {
      cluster: PUSHER_CLUSTER,
      forceTLS: PUSHER_SCHEME !== 'http',
    };

    if (PUSHER_HOST) options.wsHost = PUSHER_HOST;
    if (PUSHER_PORT) options.wsPort = Number(PUSHER_PORT);
    if (PUSHER_SCHEME) options.wsScheme = PUSHER_SCHEME;

    const pusher = new window.Pusher(PUSHER_KEY, options);
    const channel = pusher.subscribe(PLAYBACK_CHANNEL);
    channel.bind('ProgramPlaybackUpdated', load);

    return () => {
      channel.unbind('ProgramPlaybackUpdated', load);
      pusher.unsubscribe(PLAYBACK_CHANNEL);
      pusher.disconnect();
    };
  }, []);

  const current = state.current;
  const playbackProgram = current?.program ?? null;
  const playbackMedia = current?.media ?? playbackProgram ?? {};
  const mediaUrl = playbackMedia?.source_url ?? playbackProgram?.source_url ?? '';
  const mediaType =
    playbackProgram?.media_type ??
    (current?.source === 'archive' ? 'video' : null);
  const thumbnailUrl = playbackMedia?.thumbnail_url ?? playbackProgram?.thumbnail_url;
  const isLikelyVideoUrl = Boolean(mediaUrl) && /\.(mp4|m4v|webm|mov)(\?|#|$)/i.test(mediaUrl);
  const embedUrl = toEmbeddableMediaUrl(mediaUrl);
  const isVideo =
    Boolean(mediaUrl) &&
    !embedUrl &&
    (mediaType === 'video' || current?.source === 'archive' || isLikelyVideoUrl);
  const isEmbeddable =
    Boolean(embedUrl) ||
    (Boolean(mediaUrl) && ['web', 'playlist'].includes(mediaType ?? ''));

  const status = useMemo(() => {
    if (loading) return 'Checking…';
    if (!current) return 'Off-air';
    return 'Live';
  }, [current, loading]);
  const currentSlotForSeek = useMemo(
    () => ({
      starts_at: current?.starts_at ?? null,
      ends_at: current?.ends_at ?? null,
    }),
    [current?.starts_at, current?.ends_at]
  );

  useEffect(() => {
    setPlaybackMode('live');
  }, [current?.id, current?.starts_at, mediaUrl]);

  const handleVideoEnded = useCallback(async () => {
    setAutoplayBlocked(false);

    try {
      if (current?.source === 'archive') {
        await loadNextArchiveItem();
        return;
      }

      const nowMs = Date.now();
      const nextSlot = state.upcoming?.find((slot) => {
        const startsAt = new Date(slot?.starts_at ?? '').getTime();
        return Number.isFinite(startsAt) && startsAt <= nowMs + 60_000;
      });

      if (nextSlot) {
        setState((previous) => ({
          current: nextSlot,
          upcoming: previous.upcoming?.filter((slot) => slot?.id !== nextSlot.id) ?? [],
        }));
        return;
      }

      await load();
    } catch (err) {
      setError(err.response?.data?.message ?? 'Unable to load the next program.');
    }
  }, [current?.source, loadNextArchiveItem, state.upcoming]);

  useEffect(() => {
    const boundary = new Date(current?.ends_at ?? '').getTime();
    if (!Number.isFinite(boundary)) return undefined;

    const timeout = setTimeout(() => {
      handleVideoEnded();
    }, Math.max(0, boundary - Date.now()) + 250);

    return () => clearTimeout(timeout);
  }, [current?.id, current?.ends_at, handleVideoEnded]);

  useEffect(() => {
    if (!isVideo || !mediaUrl) return;
    const video = videoRef.current;
    if (!video) return;

    const attemptPlay = async () => {
      try {
        await video.play();
        setAutoplayBlocked(false);
      } catch (_) {
        // Autoplay can be blocked; user gesture will be required.
        setAutoplayBlocked(true);
      }
    };

    const handleReady = () => {
      seekVideoForMode(video, playbackMode, currentSlotForSeek);
      attemptPlay();
    };

    video.addEventListener('loadedmetadata', handleReady);
    if (video.readyState >= 1) {
      handleReady();
    }

    return () => {
      video.removeEventListener('loadedmetadata', handleReady);
    };
  }, [isVideo, mediaUrl, playbackMode, current?.id, currentSlotForSeek]);

  const schedule = useMemo(() => {
    const slots = [];
    if (current) slots.push(current);
    if (state.upcoming?.length) slots.push(...state.upcoming);
    return slots;
  }, [current, state.upcoming]);

  return (
    <div className="public-shell">
      <div className="public-hero">
        <div>
          <p className="text-muted">Kingdom Vision International Network TV</p>
          <h1>Watch live, see what&apos;s next, never miss a program.</h1>
          <p>
            This public channel shows the current broadcast, the upcoming slate, and the full calendar
            of ministry programming.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <a className="btn btn-muted" href={PUBLIC_HOMEPAGE_URL}>
            Home
          </a>
          <button type="button" className="btn btn-primary" onClick={load} disabled={loading}>
            {loading ? 'Updating…' : 'Refresh now'}
          </button>
          <Link className="btn btn-muted" to="/tv">
            TV
          </Link>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <section className="public-grid">
        <div className="card public-card broadcast-card">
          <header className="public-card__header">
            <div>
              <p className="text-muted" style={{ marginBottom: '0.25rem' }}>
                Live broadcast
              </p>
              <h2 style={{ margin: 0 }}>{current?.title ?? current?.program?.title ?? 'On-air channel'}</h2>
            </div>
            <span className="pill">{status}</span>
          </header>

          <div className="broadcast-frame">
            {isVideo ? (
              <>
                <video
                  ref={videoRef}
                  src={mediaUrl}
                  poster={thumbnailUrl}
                  style={{ width: '100%', height: '100%', background: '#000' }}
                  controls
                  autoPlay
                  playsInline
                  onEnded={handleVideoEnded}
                />
                {autoplayBlocked && (
                  <button
                    type="button"
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'grid',
                      placeItems: 'center',
                      background: 'rgba(0,0,0,0.6)',
                      color: '#fff',
                      fontSize: '1.05rem',
                      fontWeight: 600,
                      border: 'none',
                      cursor: 'pointer',
                    }}
                    className="broadcast-autoplay"
                    onClick={async () => {
                      const video = videoRef.current;
                      if (!video) return;
                      try {
                        await video.play();
                        setAutoplayBlocked(false);
                      } catch (_) {
                        setAutoplayBlocked(true);
                      }
                    }}
                  >
                    Tap to play
                  </button>
                )}
              </>
            ) : isEmbeddable ? (
              <iframe
                key={embedUrl ?? mediaUrl}
                src={embedUrl ?? mediaUrl}
                title={current?.title ?? playbackProgram?.title ?? 'Embedded program'}
                style={{ width: '100%', height: '100%', minHeight: 320, background: '#000' }}
                frameBorder="0"
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <div className="broadcast-placeholder">
                <div>
                  <p style={{ margin: 0, opacity: 0.85 }}>No video preview</p>
                  <p style={{ margin: 0, fontSize: '0.95rem' }}>
                    {mediaUrl ? 'Source is not embeddable in preview' : 'Awaiting next program'}
                  </p>
                </div>
              </div>
            )}
            <div className="broadcast-badge">{status}</div>
          </div>

          {isVideo && (
            <div className="public-card__footer" style={{ marginTop: '0.5rem' }}>
              <div className="pill-row">
                <button
                  type="button"
                  className={`btn ${playbackMode === 'live' ? 'btn-primary' : 'btn-muted'}`}
                  style={{ padding: '0.55rem 0.95rem', boxShadow: 'none' }}
                  onClick={async () => {
                    setPlaybackMode('live');
                    const video = videoRef.current;
                    if (!video) return;
                    seekVideoForMode(video, 'live', current);
                    try {
                      await video.play();
                      setAutoplayBlocked(false);
                    } catch (_) {
                      setAutoplayBlocked(true);
                    }
                  }}
                >
                  Continue from current point
                </button>
                <button
                  type="button"
                  className={`btn ${playbackMode === 'start' ? 'btn-primary' : 'btn-muted'}`}
                  style={{ padding: '0.55rem 0.95rem', boxShadow: 'none' }}
                  onClick={async () => {
                    setPlaybackMode('start');
                    const video = videoRef.current;
                    if (!video) return;
                    seekVideoForMode(video, 'start', current);
                    try {
                      await video.play();
                      setAutoplayBlocked(false);
                    } catch (_) {
                      setAutoplayBlocked(true);
                    }
                  }}
                >
                  Watch from beginning
                </button>
              </div>
            </div>
          )}

          <div className="public-card__footer">
            <div>
              <p className="text-muted" style={{ marginBottom: '0.25rem' }}>
                {current?.type === 'live' ? 'Live window' : 'Program'}
              </p>
              <strong>{current?.title ?? playbackProgram?.title ?? 'Off-air'}</strong>
            </div>
            <div className="pill-row">
              <span className="pill">Starts {formatTime(current?.starts_at)}</span>
              <span className="pill">Ends {formatTime(current?.ends_at)}</span>
              <span className="pill">{mediaType ?? current?.type ?? '—'}</span>
            </div>
          </div>
        </div>
      </section>

      <section className="card public-card schedule-card">
        <header className="public-card__header">
          <div>
            <p className="text-muted" style={{ marginBottom: '0.25rem' }}>
              Schedule
            </p>
            <h2 style={{ margin: 0 }}>Upcoming programs</h2>
          </div>
        </header>

        {loading && !schedule.length ? (
          <div style={{ padding: '1rem 0' }}>Loading schedule…</div>
        ) : schedule.length ? (
          <ul className="schedule-list">
            {schedule.map((slot) => (
              <li key={slot.id} className="schedule-list__item">
                <div>
                  <p className="text-muted" style={{ marginBottom: '0.25rem' }}>
                    {formatDay(slot.starts_at)} · {slot.type === 'live' ? 'Live' : 'Program'}
                  </p>
                  <strong>{slot.title ?? slot.program?.title ?? 'Scheduled item'}</strong>
                  {(slot.program?.description || slot.description) && (
                    <p style={{ marginTop: '0.25rem', color: 'var(--text-muted)' }}>
                      {slot.program?.description ?? slot.description}
                    </p>
                  )}
                </div>
                <div className="pill-row">
                  <span className="pill">Start {formatTime(slot.starts_at)}</span>
                  <span className="pill">End {formatTime(slot.ends_at)}</span>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="empty-state">
            <p style={{ marginBottom: '0.5rem' }}>No items scheduled yet.</p>
            <p className="text-muted">New programs will appear automatically once published.</p>
          </div>
        )}
      </section>

      {CALENDAR_EMBED_URL && (
        <section className="card public-card calendar-card">
          <header className="public-card__header">
            <div>
              <p className="text-muted" style={{ marginBottom: '0.25rem' }}>
                Calendar
              </p>
              <h2 style={{ margin: 0 }}>Full ministry calendar</h2>
            </div>
          </header>

          <div className="calendar-embed">
            <iframe
              src={CALENDAR_EMBED_URL}
              title="Ministry calendar"
              frameBorder="0"
              scrolling="no"
              style={{ width: '100%', height: '100%', border: '0' }}
            />
          </div>
        </section>
      )}
    </div>
  );
};

export default PublicChannel;
